import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import type { MyPermissions } from "@/entities/permission/api/permissionService";
import { kefuPendingService, type KefuPending } from "../api/kefuPendingService";

/** 侧栏红点、顶栏铃铛、客服舱标题共用一个 key —— 一页内只轮询一路。 */
export const KEFU_PENDING_QUERY_KEY = ["cs-pending"] as const;
export const KEFU_PENDING_POLL_MS = 30_000;

/**
 * 能不能读 `/pending`:租户身份 + 客服舱开着,或停用但历史上拥有过(契约:该口属人工处理口,舱暂停时也可读)。
 * 判据与 features/customer-service/model/kefuHumanOnly 同口径(entities 不能反向 import features,这里照抄判据)。
 */
export function canReadKefuPending(
  permissions: MyPermissions | null | undefined,
  scope: string | undefined
): boolean {
  if (scope !== "TENANT" || !permissions) return false;
  const mod = permissions.modules?.["ai_kefu"];
  if (mod && mod.page !== "none") return true;
  const pool = permissions.everOwnedModules ?? permissions.historyModules ?? [];
  return pool.includes("ai_kefu");
}

/**
 * 客服舱待处理提醒(30s 轮询;页面隐藏时 react-query 自动暂停 refetchInterval,回到前台立刻拉一次)。
 * 查不到(无权限 / 接口未上 / 出错)= data undefined,调用方一律当「没有提醒」,不报错、不弹 toast。
 */
export function useKefuPending(): KefuPending | undefined {
  const { permissions, user } = useAuth();
  // 代客态 user.scope 仍是平台 / 渠道,有效身份看 permissions.role(租户角色 = TENANT_*)
  const role = (permissions?.role as string | undefined) ?? user?.role;
  const scope = role?.startsWith("TENANT_") ? "TENANT" : user?.scope;
  const enabled = canReadKefuPending(permissions, scope);
  const { data } = useQuery({
    queryKey: KEFU_PENDING_QUERY_KEY,
    queryFn: kefuPendingService.get,
    enabled,
    refetchInterval: KEFU_PENDING_POLL_MS,
    refetchOnWindowFocus: true,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
  return enabled ? data : undefined;
}
